// useDetailsCardVault.ts
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ISecretDTO } from "../interfaces/ISecretDTO";
import IUseDetailsCardVaultProps from "../interfaces/IUseDetailsCardProps";
import { fetchPasswordVaultStatus } from "../actions/get-one-password.action";

export type SecurityStatus = "strong" | "medium" | "weak";



export const useDetailsCardVault = ({
  secretId,
  secret,
  unlocked,
  revealSeconds,
  onUpdate,
  onAfterSave,
  isVerified= false,
}: IUseDetailsCardVaultProps) => {
  const [revealed, setRevealed] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [draft, setDraft] = useState<ISecretDTO>({
    title: secret.title,
    usernameEmail: secret.usernameEmail,
    password: secret.password,
    websiteUrl: secret.websiteUrl ?? "",
    notes: secret.notes ?? "",
    imageUrl: secret.imageUrl ?? "",
    highSecurity: secret.highSecurity ?? false,
  });

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const hideNow = useCallback(() => {
    clearTimers();
    setRevealed(false);
    setRemainingSeconds(0);
  }, [clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

const secretChangeKey = useMemo(() => {
  
    return `${secretId}|${secret.modifiedAt ?? ""}|${secret.createdAt ?? ""}`;
  }, [secretId, secret.modifiedAt, secret.createdAt]);

  
  useEffect(() => {
    setIsEditing(false);
    setSaving(false);
    hideNow();

    setDraft({
      title: secret.title,
      usernameEmail: secret.usernameEmail,
      password: secret.password,
      websiteUrl: secret.websiteUrl ?? "",
      notes: secret.notes ?? "",
      imageUrl: secret.imageUrl ?? "",
      highSecurity: secret.highSecurity ?? false,
    });
  }, [secretChangeKey, hideNow]); 

  useEffect(() => {
    if (!unlocked) {
      setIsEditing(false);
      hideNow();
    }
  }, [unlocked, hideNow]);

const canReveal =
  unlocked &&
  !isEditing;
  

const handleReveal = useCallback(async (providedCode?: string, alreadyFetchedData?: any) => {
  if (!canReveal) return;
  if (revealed) { hideNow(); return; }

  try {
    
    let result = alreadyFetchedData;

    if (!result) {
      result = await fetchPasswordVaultStatus(secretId, providedCode);
    }

    if (result && "requires2FA" in result) {
      return { status: "need_2fa" };
    }

    if (result && "password" in result) {
      setRevealedPassword(result.password);
      setRevealed(true);
      setRemainingSeconds(revealSeconds);
      clearTimers();
      
      intervalRef.current = setInterval(() => {
        setRemainingSeconds((prev) => Math.max(0, prev - 1));
      }, 1000);
      
      timerRef.current = setTimeout(() => { hideNow(); }, revealSeconds * 1000);
      return { status: "success" };
    }
  } catch (err) {
    console.error("Error en reveal:", err);
    return { status: "error" };
  }
}, [canReveal, revealed, revealSeconds, clearTimers, hideNow, secretId]);


  const statusColor = (status?: SecurityStatus) => {
    switch (status) {
      case "strong":
        return "green";
      case "medium":
        return "orange";
      case "weak":
        return "red";
      default:
        return "gray";
    }
  };

  const canSave = Boolean(unlocked && isEditing && !saving && draft.title?.trim() && draft.usernameEmail?.trim() && draft.password?.trim());

  const startEdit = () => {
    if (!unlocked) return;
    setIsEditing(true);
    hideNow();
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setDraft({
      title: secret.title,
      usernameEmail: secret.usernameEmail,
      password: secret.password,
      websiteUrl: secret.websiteUrl ?? "",
      notes: secret.notes ?? "",
      imageUrl: secret.imageUrl ?? "",
      highSecurity: secret.highSecurity ?? false,
    });
  };

  const saveEdit = async () => {
    if (!onUpdate || !canSave) return;

    try {
      setSaving(true);

      const dto: ISecretDTO = {
        title: draft.title,
        usernameEmail: draft.usernameEmail,
        password: draft.password,
        websiteUrl: draft.websiteUrl ?? "",
        notes: draft.notes ?? "",
        imageUrl: draft.imageUrl ?? "",
        highSecurity: draft.highSecurity ?? false,
      };

      await onUpdate(secretId, dto);

      setIsEditing(false);
      hideNow();
      onAfterSave?.();
    } finally {
      setSaving(false);
    }
  };

  const handleDraftChange = (field: keyof ISecretDTO, value: any) => {
    setDraft((p) => ({ ...p, [field]: value }));
  };

  return {
    revealed,
    remainingSeconds,
    isEditing,
    saving,
    draft,
    revealedPassword,
    handleReveal,
    statusColor,
    canReveal,
    canSave,
    startEdit,
    cancelEdit,
    saveEdit,
    handleDraftChange,
    masked: "••••••••••••••••",
  };
};
